import React, { useState } from 'react';
import {
  Tag,
  Switch,
  InputNumber,
  Input,
  Select,
  Space,
  Typography,
  Tooltip,
  Button,
  Popover,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  SettingOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { SchemaField, SchemaConstraints } from './types';
import { STRING_FORMATS } from './types';

const { Text } = Typography;

interface SchemaConstraintEditorProps {
  fields: SchemaField[];
  onChange: (fields: SchemaField[]) => void;
  title: string;
  readOnlyStructure?: boolean;
  hideRequired?: boolean;
}

const TYPE_COLOR_MAP: Record<string, string> = {
  string: '#52c41a',
  number: '#1890ff',
  integer: '#1890ff',
  boolean: '#fa8c16',
  array: '#722ed1',
  object: '#13c2c2',
};

const SchemaConstraintEditor: React.FC<SchemaConstraintEditorProps> = ({
  fields,
  onChange,
  title,
  readOnlyStructure = true,
  hideRequired = false,
}) => {
  const updateFieldDeep = (
    fieldList: SchemaField[],
    key: string,
    updates: Partial<SchemaField>
  ): SchemaField[] => {
    return fieldList.map((f) => {
      if (f.key === key) return { ...f, ...updates };
      if (f.children) {
        return { ...f, children: updateFieldDeep(f.children, key, updates) };
      }
      return f;
    });
  };

  const updateConstraintDeep = (
    fieldList: SchemaField[],
    key: string,
    constraintUpdates: Partial<SchemaConstraints>
  ): SchemaField[] => {
    return fieldList.map((f) => {
      if (f.key === key) {
        return { ...f, constraints: { ...f.constraints, ...constraintUpdates } };
      }
      if (f.children) {
        return { ...f, children: updateConstraintDeep(f.children, key, constraintUpdates) };
      }
      return f;
    });
  };

  const updateField = (key: string, updates: Partial<SchemaField>) => {
    onChange(updateFieldDeep(fields, key, updates));
  };

  const updateConstraint = (key: string, constraintUpdates: Partial<SchemaConstraints>) => {
    onChange(updateConstraintDeep(fields, key, constraintUpdates));
  };

  const hasConstraints = (field: SchemaField): boolean => {
    const c = field.constraints;
    return !!(
      c.minLength || c.maxLength || c.pattern || c.format ||
      c.enum?.length || c.minimum !== undefined || c.maximum !== undefined ||
      c.exclusiveMinimum !== undefined || c.exclusiveMaximum !== undefined ||
      c.multipleOf !== undefined || c.minItems !== undefined ||
      c.maxItems !== undefined || c.uniqueItems
    );
  };

  if (fields.length === 0) {
    return (
      <div style={{ padding: '24px 0', textAlign: 'center', color: '#999' }}>
        暂无{title}参数定义
      </div>
    );
  }

  const renderFieldRow = (field: SchemaField, depth: number = 0) => {
    const isObject = field.type === 'object';
    const showArraySwitch = isObject;

    return (
      <div key={field.key}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '6px 12px',
            paddingLeft: 12 + depth * 20,
            borderBottom: '1px solid #f0f0f0',
            minHeight: 38,
            transition: 'background 0.15s',
          }}
          className="schema-field-row"
        >
          {/* 字段名 */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, gap: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text strong style={{ fontSize: 13, color: '#333', whiteSpace: 'nowrap' }}>
                {field.name}
              </Text>
              {field.codeName && field.codeName !== field.name && (
                <Text style={{ fontSize: 11, color: '#8c8c8c', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                  {field.codeName}
                </Text>
              )}
            </div>
            {field.description && (
              <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.3 }}>
                {field.description}
              </Text>
            )}
          </div>

          {/* 类型标签 */}
          <div style={{ width: 100, flexShrink: 0, textAlign: 'center' }}>
            <span style={{ color: TYPE_COLOR_MAP[field.type] || '#999', fontFamily: 'monospace', fontSize: 12 }}>
              {field.isArray ? `${field.type}[]` : field.type}
            </span>
          </div>

          {/* 快捷标记区 */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, width: 120, justifyContent: 'flex-end' }}>
            {/* 必填标记 — 输出参数不展示 */}
            {!hideRequired && (
              <Tooltip title={field.required ? '必填' : '可选'}>
                <span
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 3,
                    fontSize: 14,
                    fontWeight: 'bold',
                    color: field.required ? '#ff4d4f' : '#d9d9d9',
                    transition: 'color 0.15s',
                  }}
                  onClick={() => updateField(field.key, { required: !field.required })}
                >
                  *
                </span>
              </Tooltip>
            )}

            {/* 数组标记 — 仅 object 类型 */}
            {showArraySwitch && (
              <Tooltip title={field.isArray ? '数组(多个)' : '单个'}>
                <span
                  style={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 20,
                    height: 20,
                    borderRadius: 3,
                    fontSize: 11,
                    fontFamily: 'monospace',
                    fontWeight: 'bold',
                    color: field.isArray ? '#722ed1' : '#d9d9d9',
                    transition: 'color 0.15s',
                  }}
                  onClick={() => updateField(field.key, { isArray: !field.isArray })}
                >
                  []
                </span>
              </Tooltip>
            )}

            {/* 约束编辑按钮 — 非 object/boolean 才显示 */}
            {field.type !== 'object' && field.type !== 'boolean' && (
              <Popover
                trigger="click"
                placement="rightTop"
                overlayStyle={{ maxWidth: 340 }}
                content={
                  <ConstraintPanel
                    field={field}
                    hideRequired={hideRequired}
                    onFieldUpdate={(updates) => updateField(field.key, updates)}
                    onConstraintUpdate={(updates) => updateConstraint(field.key, updates)}
                  />
                }
              >
                <Tooltip title="编辑约束">
                  <span
                    style={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 20,
                      height: 20,
                      borderRadius: 3,
                      fontSize: 12,
                      color: hasConstraints(field) ? '#1890ff' : '#d9d9d9',
                      transition: 'color 0.15s',
                    }}
                  >
                    <SettingOutlined />
                  </span>
                </Tooltip>
              </Popover>
            )}
          </div>
        </div>

        {/* 递归渲染子字段 */}
        {field.children && field.children.length > 0 && (
          <div>
            {field.children.map((child) => renderFieldRow(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
      {/* 表头 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          background: '#fafafa',
          borderBottom: '1px solid #f0f0f0',
          fontSize: 12,
          color: '#999',
          fontWeight: 500,
        }}
      >
        <div style={{ flex: 1 }}>字段名</div>
        <div style={{ width: 100, textAlign: 'center', flexShrink: 0 }}>类型</div>
        <div style={{ width: 120, textAlign: 'right', flexShrink: 0 }}>操作</div>
      </div>
      {/* 字段列表 */}
      {fields.map((field) => renderFieldRow(field, 0))}
    </div>
  );
};

/**
 * 约束编辑面板（Popover 内容）
 */
const ConstraintPanel: React.FC<{
  field: SchemaField;
  hideRequired?: boolean;
  onFieldUpdate: (updates: Partial<SchemaField>) => void;
  onConstraintUpdate: (updates: Partial<SchemaConstraints>) => void;
}> = ({ field, hideRequired = false, onFieldUpdate, onConstraintUpdate }) => {
  const { type, constraints } = field;

  return (
    <div style={{ width: 300 }}>
      <div style={{ marginBottom: 8 }}>
        <Text strong style={{ fontSize: 13 }}>{field.name}</Text>
        <span style={{ color: TYPE_COLOR_MAP[field.type], marginLeft: 8, fontSize: 12, fontFamily: 'monospace' }}>
          {field.type}
        </span>
      </div>

      <Divider style={{ margin: '8px 0' }} />

      {/* 开关行 */}
      {!hideRequired && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
            <Switch
              size="small"
              checked={field.required || false}
              onChange={(v) => onFieldUpdate({ required: v })}
            />
            <span>必需</span>
          </label>
        </div>
      )}

      {/* string 类型约束 */}
      {type === 'string' && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>format</Text>
            <Select
              size="small"
              placeholder="选择格式"
              value={constraints.format || undefined}
              onChange={(v) => onConstraintUpdate({ format: v || undefined })}
              options={STRING_FORMATS}
              allowClear
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>最小长度</Text>
              <InputNumber
                size="small"
                placeholder=">= 0"
                min={0}
                value={constraints.minLength}
                onChange={(v) => onConstraintUpdate({ minLength: v ?? undefined })}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>最大长度</Text>
              <InputNumber
                size="small"
                placeholder=">= 0"
                min={0}
                value={constraints.maxLength}
                onChange={(v) => onConstraintUpdate({ maxLength: v ?? undefined })}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>pattern</Text>
            <Input
              size="small"
              placeholder="^[A-Za-z0-9_]+$"
              value={constraints.pattern}
              onChange={(e) => onConstraintUpdate({ pattern: e.target.value || undefined })}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>

          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>枚举值</Text>
            <EnumEditor
              values={constraints.enum || []}
              onChange={(vals) => onConstraintUpdate({ enum: vals.length > 0 ? vals : undefined })}
            />
          </div>
        </>
      )}

      {/* number / integer 类型约束 */}
      {(type === 'number' || type === 'integer') && (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>最小值</Text>
              <InputNumber
                size="small"
                placeholder="minimum"
                value={constraints.minimum}
                onChange={(v) => onConstraintUpdate({ minimum: v ?? undefined })}
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>最大值</Text>
              <InputNumber
                size="small"
                placeholder="maximum"
                value={constraints.maximum}
                onChange={(v) => onConstraintUpdate({ maximum: v ?? undefined })}
                style={{ width: '100%' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
              <Switch
                size="small"
                checked={constraints.exclusiveMinimum !== undefined}
                onChange={(v) => onConstraintUpdate({
                  exclusiveMinimum: v ? (constraints.minimum ?? 0) : undefined,
                })}
              />
              <span>排除最小值</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
              <Switch
                size="small"
                checked={constraints.exclusiveMaximum !== undefined}
                onChange={(v) => onConstraintUpdate({
                  exclusiveMaximum: v ? (constraints.maximum ?? 0) : undefined,
                })}
              />
              <span>排除最大值</span>
            </label>
          </div>

          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>倍数 (multipleOf)</Text>
            <InputNumber
              size="small"
              placeholder="multipleOf"
              min={0}
              value={constraints.multipleOf}
              onChange={(v) => onConstraintUpdate({ multipleOf: v ?? undefined })}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: 4 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>枚举值</Text>
            <EnumEditor
              values={constraints.enum || []}
              onChange={(vals) => onConstraintUpdate({ enum: vals.length > 0 ? vals : undefined })}
            />
          </div>
        </>
      )}

      {/* array / isArray 数量约束 */}
      {(type === 'array' || field.isArray) && (
        <>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>数量限制</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <InputNumber
                size="small"
                placeholder="最少"
                min={0}
                value={constraints.minItems ?? null}
                onChange={(v) => onConstraintUpdate({ minItems: v ?? undefined })}
                style={{ width: 80 }}
              />
              <span style={{ fontSize: 12, color: '#999' }}>~</span>
              <InputNumber
                size="small"
                placeholder="无限制"
                min={constraints.minItems ?? 0}
                value={constraints.maxItems ?? null}
                onChange={(v) => onConstraintUpdate({ maxItems: v ?? undefined })}
                style={{ width: 80 }}
              />
              <span style={{ fontSize: 11, color: '#999' }}>个</span>
              {(constraints.minItems != null || constraints.maxItems != null) && (
                <Button
                  type="link"
                  size="small"
                  style={{ fontSize: 11, padding: 0 }}
                  onClick={() => onConstraintUpdate({ minItems: undefined, maxItems: undefined })}
                >
                  无限制
                </Button>
              )}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
            <Switch
              size="small"
              checked={constraints.uniqueItems || false}
              onChange={(v) => onConstraintUpdate({ uniqueItems: v || undefined })}
            />
            <span>元素唯一</span>
          </label>
        </>
      )}
    </div>
  );
};

/**
 * 枚举值编辑器
 */
const EnumEditor: React.FC<{
  values: string[];
  onChange: (values: string[]) => void;
}> = ({ values, onChange }) => {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (val: string) => {
    onChange(values.filter((v) => v !== val));
  };

  return (
    <div>
      {values.length > 0 && (
        <Space size={4} wrap style={{ marginBottom: 4 }}>
          {values.map((val) => (
            <Tag
              key={val}
              closable
              onClose={() => handleRemove(val)}
              style={{ fontSize: 11 }}
            >
              {val}
            </Tag>
          ))}
        </Space>
      )}
      <Space size={4}>
        <Input
          size="small"
          placeholder="添加枚举值"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleAdd}
          style={{ width: 130, fontSize: 12 }}
        />
        <Button
          size="small"
          type="text"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          style={{ fontSize: 11 }}
        />
      </Space>
    </div>
  );
};

export default SchemaConstraintEditor;
